/**
 * useContactTags Hook
 *
 * Handles tag management for contacts in the inbox-v3 feature.
 * Allows fetching all tags, adding/removing tags from contacts, and creating new tags.
 */

import {useState, useCallback, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useGetAllTagsQuery, useCreateTagMutation} from '../../../../../services/cellcast'
import {getIsDefaultRegion} from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {assignetagsContacts, UnassignetagsContacts} from '../../Contacts/Tags/TagSlice/TagsSlices'

// ============================================================================
// Types
// ============================================================================

export interface Tag {
  _id: string
  name: string
  color?: string
  type?: string
  isHidden?: boolean
}

export interface TagOption {
  value: string
  label: string
  color?: string
}

interface UseContactTagsOptions {
  /** Contact ID to manage tags for */
  contactId: string | null
  /** Current tags assigned to the contact */
  currentTags?: Array<{name: string; isHidden?: boolean; type?: string; _id?: string}>
  /** Callback when tags are updated */
  onTagsUpdated?: () => void
}

interface UseContactTagsReturn {
  // All available tags
  allTags: Tag[]
  tagOptions: TagOption[]
  isLoadingTags: boolean

  // Selected tags state
  selectedTags: TagOption[]
  setSelectedTags: (tags: TagOption[]) => void

  // Actions
  addTagsToContact: (tagIds: string[]) => Promise<boolean>
  removeTagsFromContact: (tagIds: string[]) => Promise<boolean>
  updateContactTags: (tagIds: string[]) => Promise<boolean>
  createNewTag: (name: string, color?: string) => Promise<Tag | null>

  // State
  isUpdating: boolean
  isCreating: boolean
  error: string | null
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useContactTags(options: UseContactTagsOptions): UseContactTagsReturn {
  const {contactId, currentTags = [], onTagsUpdated} = options

  // State
  const [selectedTags, setSelectedTags] = useState<TagOption[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  // Redux
  const dispatch = useDispatch()
  const isDefaultRegion = useSelector(getIsDefaultRegion)

  // RTK Query hooks
  const {data: tagsResponse, isLoading: isLoadingTags} = useGetAllTagsQuery({})
  const [createTagMutation, {isLoading: isCreating}] = useCreateTagMutation()

  // ============================================================================
  // Computed Values
  // ============================================================================

  /**
   * All available tags (filtered to remove hidden tags and lists)
   */
  const allTags = useMemo<Tag[]>(() => {
    const rawTags = tagsResponse?.data || []
    return rawTags.filter((tag: Tag) => {
      // Filter out hidden tags
      if (tag.isHidden) return false
      // Filter out 'list' type tags
      if (tag.type === 'list') return false
      return true
    })
  }, [tagsResponse])

  /**
   * Tag options for dropdown/select
   */
  const tagOptions = useMemo<TagOption[]>(() => {
    return allTags.map((tag) => ({
      value: tag._id,
      label: tag.name,
      color: tag.color,
    }))
  }, [allTags])

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Add tags to contact using the same API as left panel
   * Uses /api/v1/group/assignTagsToContact/ endpoint
   */
  const addTagsToContact = useCallback(
    async (tagIds: string[]): Promise<boolean> => {
      if (!contactId || tagIds.length === 0) return false

      setError(null)
      setIsUpdating(true)

      try {
        await dispatch(
          assignetagsContacts({
            data: {
              contactIds: [contactId],
              tagId: tagIds,
            },
          }) as any
        ).unwrap()

        onTagsUpdated?.()
        return true
      } catch (err: any) {
        console.error('Error adding tags to contact:', err)
        setError(err?.message || 'Failed to add tags')
        return false
      } finally {
        setIsUpdating(false)
      }
    },
    [contactId, dispatch, onTagsUpdated]
  )

  /**
   * Remove tags from contact using the same API as left panel
   * Uses /api/v1/group/unAssignTagsFromContact/ endpoint
   */
  const removeTagsFromContact = useCallback(
    async (tagIds: string[]): Promise<boolean> => {
      if (!contactId || tagIds.length === 0) return false

      setError(null)
      setIsUpdating(true)

      try {
        await dispatch(
          UnassignetagsContacts({
            contactIds: [contactId],
            tagId: tagIds,
          }) as any
        ).unwrap()

        onTagsUpdated?.()
        return true
      } catch (err: any) {
        console.error('Error removing tags from contact:', err)
        setError(err?.message || 'Failed to remove tags')
        return false
      } finally {
        setIsUpdating(false)
      }
    },
    [contactId, dispatch, onTagsUpdated]
  )

  /**
   * Update contact tags (replaces current tags with new ones)
   * Calculates the diff and applies add/remove operations
   */
  const updateContactTagsFn = useCallback(
    async (newTagIds: string[]): Promise<boolean> => {
      if (!contactId) return false

      setError(null)
      setIsUpdating(true)

      // Get current tag IDs (we need to find matching tags from allTags by name)
      const currentTagIds: string[] = []
      currentTags.forEach((currentTag) => {
        // If the current tag has an _id, use it directly
        if (currentTag._id) {
          currentTagIds.push(currentTag._id)
        } else {
          // Otherwise, try to find it by name in allTags
          const matchingTag = allTags.find(
            (t) => t.name.toLowerCase() === currentTag.name.toLowerCase()
          )
          if (matchingTag) {
            currentTagIds.push(matchingTag._id)
          }
        }
      })

      // Calculate diff
      const tagsToAdd = newTagIds.filter((id) => !currentTagIds.includes(id))
      const tagsToRemove = currentTagIds.filter((id) => !newTagIds.includes(id))

      try {
        // Execute add and remove operations
        const promises: Promise<any>[] = []

        if (tagsToAdd.length > 0) {
          promises.push(
            dispatch(
              assignetagsContacts({
                data: {
                  contactIds: [contactId],
                  tagId: tagsToAdd,
                },
              }) as any
            ).unwrap()
          )
        }

        if (tagsToRemove.length > 0) {
          promises.push(
            dispatch(
              UnassignetagsContacts({
                contactIds: [contactId],
                tagId: tagsToRemove,
              }) as any
            ).unwrap()
          )
        }

        if (promises.length > 0) {
          await Promise.all(promises)
        }

        onTagsUpdated?.()
        return true
      } catch (err: any) {
        console.error('Error updating contact tags:', err)
        setError(err?.message || 'Failed to update tags')
        return false
      } finally {
        setIsUpdating(false)
      }
    },
    [contactId, currentTags, allTags, dispatch, onTagsUpdated]
  )

  /**
   * Create a new tag
   */
  const createNewTag = useCallback(
    async (name: string, color?: string): Promise<Tag | null> => {
      if (!name.trim()) return null

      setError(null)

      try {
        const response = await createTagMutation({
          name: name.trim(),
          color,
        }).unwrap()

        if (response?.data) {
          return response.data
        }
        return null
      } catch (err: any) {
        console.error('Error creating tag:', err)
        setError(err?.data?.message || 'Failed to create tag')
        return null
      }
    },
    [createTagMutation]
  )

  // ============================================================================
  // Return
  // ============================================================================

  return {
    // All available tags
    allTags,
    tagOptions,
    isLoadingTags,

    // Selected tags state
    selectedTags,
    setSelectedTags,

    // Actions
    addTagsToContact,
    removeTagsFromContact,
    updateContactTags: updateContactTagsFn,
    createNewTag,

    // State
    isUpdating,
    isCreating,
    error,
  }
}